import { zonedDate } from '../../utils/date-time';
import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import AvatarUser from '../../components/AvatarUser';
import { StatusHorario, StatusHorarioLabels } from '../agenda/Enum';
import { getScheduleTone } from '../agenda/cliente/List/helpers';
import { formatPhone } from '../clientes/helpers';
import { OfertasHorarioService } from './Service';
import {
    formatOfferCurrency,
    formatOfferDay,
    formatOfferRange,
    getOfferStatus,
    getRecipientName,
    getRecipientStatus,
    sortRecipients,
} from './helpers';
import './OfertasHorario.css';
import { ChevronRight, TriangleAlert } from 'lucide-react';

const service = new OfertasHorarioService();

export function OfferDetails({
    offerId,
    onOpenSchedule,
}: {
    offerId: string;
    onOpenSchedule: (horarioId: string) => void;
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [details, setDetails] = useState<any>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);
            const { data } = await service.details(offerId);
            setDetails(data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [offerId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="sf-skeleton h-20" />
                <div className="sf-skeleton h-40" />
            </div>
        );
    }

    if (error || !details?.offer) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar esta oferta</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    const { offer, metrics } = details;
    const recipients: any[] = sortRecipients(details.recipients || []);
    const horarios: any[] = details.horarios || [];
    const status = getOfferStatus(offer);
    const discount = Number(offer.discountPercent || 0);

    return (
        <div className="ofe-detail-wrap">
            <div className="ofe-detail">
                <div className="ofe-detail-side">
                    <div className="sf-group">
                        <div className="sf-info-row">
                            <span>Dia</span>
                            <span>{formatOfferDay(offer.date, offer.timezone)}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Faixa</span>
                            <span>{formatOfferRange(offer)}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Atendente</span>
                            <span>{offer.attendant?.displayName || 'Não identificado'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Desconto</span>
                            <span>{discount > 0 ? `${discount}%` : 'Sem desconto'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Situação</span>
                            <span>
                                <span className="sf-tag" data-tone={status.tone}>
                                    {status.label}
                                </span>
                            </span>
                        </div>
                    </div>

                    <div>
                        <h3 className="sf-group-title">Resultado</h3>
                        <div className="sf-group">
                            <div className="sf-info-row">
                                <span>Receita recuperada</span>
                                <span>{formatOfferCurrency(metrics?.recoveredRevenue)}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Minutos preenchidos</span>
                                <span>{Number(metrics?.filledMinutes || 0)} min</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Enviadas</span>
                                <span>{Number(metrics?.sent || 0)}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Abriram</span>
                                <span>{Number(metrics?.opened || 0)}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Agendaram</span>
                                <span>
                                    {Number(metrics?.booked || 0)} ({Math.round(Number(metrics?.conversionRate || 0) * 100)}%)
                                </span>
                            </div>
                            <div className="sf-info-row">
                                <span>Sem interesse</span>
                                <span>{Number(metrics?.noInterest || 0)}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Sem vaga ao tentar</span>
                                <span>{Number(metrics?.unavailable || 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ofe-detail-main">
                    {horarios.length > 0 && (
                        <section>
                            <h3 className="sf-group-title">Agendamentos gerados</h3>
                            <ul className="sf-group">
                                {horarios.map((horario) => (
                                    <li key={horario._id}>
                                        <button
                                            type="button"
                                            className="ofe-row is-link"
                                            onClick={() => onOpenSchedule(String(horario._id))}
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="ofe-title">
                                                    {zonedDate(horario.startAt, offer.timezone).format('HH:mm')} às {zonedDate(horario.endAt, offer.timezone).format('HH:mm')}
                                                </span>
                                                <span className="ofe-sub">
                                                    {horario.servicoName || 'Serviço'}
                                                    {horario.client?.displayName ? ` · ${horario.client.displayName}` : ''}
                                                </span>
                                                <span className="sf-tags">
                                                    <span className="sf-tag" data-tone={getScheduleTone(horario.status)}>
                                                        {StatusHorarioLabels[horario.status as StatusHorario] || horario.status}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="ofe-money">{formatOfferCurrency(horario.totalPrice)}</span>
                                            <ChevronRight className="ofe-chevron" aria-hidden />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <h3 className="sf-group-title">Destinatários</h3>
                        {recipients.length === 0 ? (
                            <div className="sf-group ofe-empty-group">Nenhum destinatário registrado.</div>
                        ) : (
                            <ul className="sf-group">
                                {recipients.map((recipient) => {
                                    const name = getRecipientName(recipient);
                                    const recipientStatus = getRecipientStatus(recipient);
                                    const phone = String(recipient?.client?.phoneNumber || '').trim();
                                    const content = (
                                        <>
                                            <AvatarUser name={name} photoURL={undefined} size="h-9 w-9 !text-xs" />
                                            <span className="min-w-0 flex-1">
                                                <span className="ofe-title">{name}</span>
                                                {!!phone && phone !== name && <span className="ofe-sub">{formatPhone(phone)}</span>}
                                            </span>
                                            <span className="sf-tag shrink-0" data-tone={recipientStatus.tone}>
                                                {recipientStatus.label}
                                            </span>
                                        </>
                                    );

                                    return (
                                        <li key={recipient._id}>
                                            {recipient.bookingHorarioId ? (
                                                <button
                                                    type="button"
                                                    className="ofe-row is-link"
                                                    onClick={() => onOpenSchedule(String(recipient.bookingHorarioId))}
                                                >
                                                    {content}
                                                    <ChevronRight className="ofe-chevron" aria-hidden />
                                                </button>
                                            ) : (
                                                <div className="ofe-row">{content}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
